/**
 * Add to Cart and Open Cart Action
 * Triggered by: arc:action:addToCartAndCartOpen
 */

import { addToCartMutation } from '@src/hooks/useShoppingBasketCart';

export const runAddToCartAndCartOpen = async (data = {}) => {
  try {
    // Handle both single object and array of objects
    const items = Array.isArray(data) ? data : [data];

    // Process each item and ensure required fields
    const processedItems = items.map(item => {
      let { variantId, quantity = 1, properties = {} } = item;

      // If no variantId provided, try to get it from product context
      if (!variantId) {
        const productContext = window.getProductContext?.();
        if (productContext && productContext.variantId) {
          variantId = productContext.variantId;
        }
      }

      if (!variantId) {
        throw new Error('Variant ID is required and could not be determined from product context');
      }

      return { variantId, quantity, properties };
    });

    // Dispatch loading start event for all items
    window.dispatchEvent(
      new CustomEvent('cart:add_loading_start', {
        detail: {
          items: processedItems,
          // Keep backward compatibility for single item
          ...(processedItems.length === 1 ? processedItems[0] : {})
        }
      })
    );

    // Add to cart
    const addToCartData = processedItems.map(({ variantId, quantity, properties }) => ({
      variantId,
      quantity,
      properties
    }));

    const result = await addToCartMutation('/cart/add.js', {
      arg: addToCartData
    });

    // Dispatch loading end event
    window.dispatchEvent(
      new CustomEvent('cart:add_loading_end', {
        detail: {
          items: processedItems,
          success: true,
          // Keep backward compatibility for single item
          ...(processedItems.length === 1 ? processedItems[0] : {})
        }
      })
    );

    // Dispatch success event - this will trigger useShoppingBasket to refresh and open minicart
    window.dispatchEvent(
      new CustomEvent('cart:item_added_and_open', {
        detail: {
          item: result,
          items: processedItems,
          // Keep backward compatibility for single item
          ...(processedItems.length === 1 ? processedItems[0] : {})
        }
      })
    );

    return {
      success: true,
      item: result,
      items: processedItems,
      message: `${processedItems.length} item${processedItems.length > 1 ? 's' : ''} added to cart successfully`
    };
  } catch (error) {
    console.error('Error adding to cart:', error);

    // Get items for error handling
    const items = Array.isArray(data) ? data : [data];

    // Dispatch loading end event for error case
    window.dispatchEvent(
      new CustomEvent('cart:add_loading_end', {
        detail: {
          items: items,
          success: false,
          error: error.message,
          // Keep backward compatibility for single item
          ...(items.length === 1 ? items[0] : {})
        }
      })
    );

    // Dispatch error event
    window.dispatchEvent(
      new CustomEvent('cart:add_error', {
        detail: {
          error: error.message,
          items: items,
          // Keep backward compatibility for single item
          ...(items.length === 1 ? items[0] : {})
        }
      })
    );

    return {
      success: false,
      error: error.message
    };
  }
};

export default runAddToCartAndCartOpen;
